import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Stack,
  CircularProgress,
} from '@mui/material';
import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import { useLocalization } from '@/hooks/useLocalization';
import { ReconnectAggregatorIntegration } from './ReconnectAggregatorIntegration';
import { ReconnectYodleeIntegration } from './ReconnectYodleeIntegration';
import type { AccountConnectionError } from '@/hooks/useAccountConnectionError';
import { isGenericCredentialsAggregator } from '@/utils/aggregatorCredentials';
import { isConsumerSiteRequired } from '@/utils/connectionError';

interface ConnectionErrorModalProps {
  open: boolean;
  onClose: () => void;
  institutionName: string;
  connectionError: AccountConnectionError;
  onContactSupport: () => void;
}

type View = 'message' | 'reconnect' | 'processing' | 'complete' | 'error';

export const ConnectionErrorModal: React.FC<ConnectionErrorModalProps> = ({
  open,
  onClose,
  institutionName,
  connectionError,
  onContactSupport,
}) => {
  const { t } = useLocalization();
  const [view, setView] = useState<View>('message');

  const handleClose = () => {
    setView('message');
    onClose();
  };

  const handleContactSupport = () => {
    setView('message');
    onContactSupport();
  };

  const showYodleeFlow =
    !isGenericCredentialsAggregator(connectionError.aggregatorId) &&
    (view === 'reconnect' || view === 'processing');

  const showGoToWebsite =
    view === 'message' &&
    isConsumerSiteRequired(connectionError.actionType) &&
    connectionError.institutionUrl.length > 0;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth='sm'
    >
      <DialogTitle>{t('connectionError.title')}</DialogTitle>
      <DialogContent>
        {view === 'message' && (
          <Typography variant='body1'>{connectionError.message}</Typography>
        )}
        {view === 'reconnect' &&
          (isGenericCredentialsAggregator(connectionError.aggregatorId) ? (
            // ByAll, Demo: the backend describes the form, we render it.
            // No onProcessing here on purpose: switching to the 'processing'
            // view would unmount this subtree mid-submit and discard the next
            // form, breaking MFA and retry. Submit feedback stays in the form.
            <ReconnectAggregatorIntegration
              connectionError={connectionError}
              institutionName={institutionName}
              onCancel={() => {
                setView('message');
              }}
              onComplete={() => {
                setView('complete');
              }}
              onError={() => {
                setView('error');
              }}
            />
          ) : null)}
        {showYodleeFlow && (
          // Yodlee: FastLink is an opaque iframe, so it keeps its own flow.
          // Stays mounted through 'processing' — switching to that view
          // used to unmount this subtree (and FastLinkIntegration beneath
          // it) right as it started waiting for yodlee.js's real
          // onSuccess/onError/onExit confirmation, tearing down the OAuth
          // safety timeout and leaving no listener to ever reach
          // 'complete'/'error' (EUD-818). Only visually hidden here; it
          // still drives its own onProcessing/onComplete/onError/onCancel
          // callbacks, which is what actually moves `view` along.
          <div style={view === 'processing' ? { display: 'none' } : undefined}>
            <ReconnectYodleeIntegration
              institutionName={institutionName}
              connectionError={connectionError}
              onProcessing={() => {
                setView('processing');
              }}
              onComplete={() => {
                setView('complete');
              }}
              onError={() => {
                setView('error');
              }}
              onCancel={() => {
                setView('message');
              }}
            />
          </div>
        )}
        {view === 'processing' && (
          <Stack
            alignItems='center'
            gap={2}
            py={2}
          >
            <CircularProgress />
            <Typography variant='h6'>
              {t('connectionError.processing.title')}
            </Typography>
            <Typography
              variant='body2'
              textAlign='center'
            >
              {t('connectionError.processing.message')}
            </Typography>
          </Stack>
        )}
        {view === 'complete' && (
          <Stack
            alignItems='center'
            gap={2}
            py={2}
          >
            <CheckCircleOutline
              color='success'
              fontSize='large'
            />
            <Typography variant='h6'>
              {t('connectionError.complete.title')}
            </Typography>
            <Typography
              variant='body2'
              textAlign='center'
            >
              {t('connectionError.complete.message')}
            </Typography>
          </Stack>
        )}
        {view === 'error' && (
          <Typography
            variant='body1'
            color='error'
          >
            {t('connectionError.processingError')}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {view !== 'complete' && (
          <Button
            variant='outlined'
            onClick={handleContactSupport}
          >
            {t('connectionError.contactSupport')}
          </Button>
        )}
        {showGoToWebsite && (
          <Button
            variant='contained'
            color='primary'
            component='a'
            href={connectionError.institutionUrl}
            target='_blank'
            rel='noopener noreferrer'
          >
            {t('connectionError.goToWebsite')}
          </Button>
        )}
        {connectionError.isActionable && view === 'message' && (
          <Button
            variant='contained'
            onClick={() => {
              setView('reconnect');
            }}
          >
            {t('connectionError.fixIt')}
          </Button>
        )}
        {(view === 'processing' || view === 'complete' || view === 'error') && (
          <Button
            variant='contained'
            onClick={handleClose}
          >
            {t('common.close')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
